import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedCommonListingDetails,
  storefrontAccessDecisionQuery,
} from '@b-stock/fe-core/bstock-next/queries'

import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { isAttStorefront } from '@/components/termsRestrictions/termsPoints/storefrontSlugs'
import { FeatureFlags } from '@/helpers/featureFlags'
import { PermissionGranule, usePermission } from '@/helpers/permissions'

import { type AttAccessRoute, toAttAccessRoute } from './attAccessRoute'

type AttAccessListing = Pick<
  OrchestratedCommonListingDetails,
  'accountId' | 'seller'
>

/**
 * The signed-in buyer's account id when the `att-key-buyer-access` flag is on, else null: a buyer
 * can accept terms and has no permission to view without accepting them. Everyone else keeps
 * today's flow and the AT&T access decision is never asked for.
 */
const useAttAccessBuyerAccountId = (): string | null => {
  const accountId = useAccountId()
  const canViewWithoutAcceptingTerms = usePermission(
    PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS
  )
  const canAcceptTerms = usePermission(
    PermissionGranule.FEBP_LISTING_TERMS_ACCEPT
  )
  const isAttAccessEnabled = useFlag(FeatureFlags.ATT_KEY_BUYER_ACCESS)

  const isBuyer = !!accountId && canAcceptTerms && !canViewWithoutAcceptingTerms
  return isBuyer && isAttAccessEnabled ? accountId : null
}

/**
 * The buyer's account id when this listing needs the AT&T access decision, else null: a
 * signed-in buyer (can accept terms, no bypass, not the listing's own seller) on an AT&T
 * storefront with the `att-key-buyer-access` flag on. Everyone else keeps today's flow and the
 * decision is never asked for.
 */
export const useAttAccessCheckAccountId = (
  listing: AttAccessListing
): string | null => {
  const buyerAccountId = useAttAccessBuyerAccountId()
  const needsCheck =
    !!buyerAccountId &&
    buyerAccountId !== listing.accountId &&
    isAttStorefront(listing.seller.storefront?.slug)
  return needsCheck ? buyerAccountId : null
}

/**
 * The buyer's AT&T access route for analytics on this listing, read from the access decision
 * the listing guard already cached (same query key). Only a buyer the guard checks reads it; for
 * everyone else the query stays disabled, so no request is made and the route is undefined.
 * `isLoading` is true only while a checked buyer's decision is still being fetched.
 */
export const useAttAccessRoute = (
  listing: AttAccessListing
): { attAccessRoute: AttAccessRoute | undefined; isLoading: boolean } => {
  const accountId = useAttAccessCheckAccountId(listing)
  const decision = storefrontAccessDecisionQuery.useWithOpts(
    [accountId ?? ''],
    {
      enabled: !!accountId,
    }
  )
  return {
    attAccessRoute: toAttAccessRoute(decision.data),
    isLoading: decision.isLoading,
  }
}

/**
 * The buyer's AT&T access route for one event that covers several listings (bulk bid). The
 * decision is per account, so the route is the buyer's: read only when `hasAttListing` and the
 * buyer is one the listing guard would check (same query key, so a cached decision is reused).
 * Undefined when no listing is AT&T, or the decision is not enforced, still loading or failed.
 */
export const useBuyerAttAccessRoute = (
  hasAttListing: boolean
): AttAccessRoute | undefined => {
  const buyerAccountId = useAttAccessBuyerAccountId()
  const accountId = hasAttListing ? buyerAccountId : null
  const decision = storefrontAccessDecisionQuery.useWithOpts(
    [accountId ?? ''],
    {
      enabled: !!accountId,
    }
  )
  if (!accountId || decision.isError) return undefined
  return toAttAccessRoute(decision.data)
}
